import type { EnemyType, SectorModifier } from "../types"
import { MAX_WAVES } from "./constants"

export type WaveBand = "opening" | "pressure" | "climax" | "boss"

export interface WaveSpawnEntry {
  enemy: EnemyType
  spawnRate: number
}

export interface WaveConfig {
  opening: WaveSpawnEntry[]
  pressure?: WaveSpawnEntry[]
  climax?: WaveSpawnEntry[]
  boss?: EnemyType
  spawnBatchSize?: number
  populationCap?: number
}

export interface SectorBossDef {
  type: EnemyType
  name: string
  intro: string
  /** Final boss HP for this sector encounter. This value is not wave-scaled. */
  hp: number
  damageMultiplier: number
  pointsMultiplier: number
  introScale: number
}

export interface SectorDefinition {
  id: string
  name: string
  description: string
  waveStart: number
  waveEnd: number
  scrollMultiplier: number
  enemyDensityMultiplier: number
  treatMultiplier: number
  bossWave: number
  waves: WaveConfig[]
  boss: SectorBossDef
  /** Multiplier applied to base bumper spawn interval (lower = more bumpers). */
  bumperDensityMultiplier: number
  /** Optional sector modifier that gives this sector a unique mechanical identity. */
  modifier?: SectorModifier
  spawnBatchSize?: number
  populationCap?: number
}

const WAVE_BAND_BREAKPOINTS = {
  openingEnd: 0.33,
  pressureEnd: 0.66,
  bossStart: 0.72,
} as const

function resolvePool(config: WaveConfig, band: WaveBand): WaveSpawnEntry[] {
  if (band === "boss") {
    if (config.boss) {
      return [{ enemy: config.boss, spawnRate: 1 }]
    }
    return config.climax ?? config.pressure ?? config.opening
  }

  if (band === "climax") {
    return config.climax ?? config.pressure ?? config.opening
  }

  if (band === "pressure") {
    return config.pressure ?? config.opening
  }

  return config.opening
}

function pickWeightedEnemy(entries: WaveSpawnEntry[]): EnemyType {
  const totalWeight = entries.reduce((sum, entry) => sum + entry.spawnRate, 0)
  if (totalWeight <= 0) {
    return entries[0]?.enemy ?? "orbDrone"
  }

  let roll = Math.random() * totalWeight
  for (const entry of entries) {
    roll -= entry.spawnRate
    if (roll <= 0) {
      return entry.enemy
    }
  }

  return entries[entries.length - 1]?.enemy ?? "orbDrone"
}

export const getWaveBandForProgress = (
  wave: number,
  waveProgress: number,
): WaveBand => {
  if (isBossWave(wave) && waveProgress >= WAVE_BAND_BREAKPOINTS.bossStart) {
    return "boss"
  }

  if (wave === 1) {
    return "opening"
  }

  if (wave === 2) {
    return waveProgress < 0.5 ? "opening" : "pressure"
  }

  if (waveProgress < WAVE_BAND_BREAKPOINTS.openingEnd) {
    return "opening"
  }

  if (waveProgress < WAVE_BAND_BREAKPOINTS.pressureEnd) {
    return "pressure"
  }

  return "climax"
}

// ── SECTOR 1: OCCUPIED SKYLINE (waves 1-4, boss: humanoid) ───

const OCCUPIED_SKYLINE: SectorDefinition = {
  id: "occupied-skyline",
  name: "Occupied Skyline",
  description: "Urban airspace under APEX control.",
  waveStart: 1,
  waveEnd: 4,
  scrollMultiplier: 1.08,
  enemyDensityMultiplier: 1.0,
  treatMultiplier: 0.95,
  bossWave: 4,
  bumperDensityMultiplier: 0.95,
  waves: [
    {
      opening: [
        { enemy: "orbDrone", spawnRate: 0.4 },
        { enemy: "hornetDrone", spawnRate: 0.1 },
      ],
      pressure: [
        { enemy: "orbDrone", spawnRate: 0.2 },
        { enemy: "hornetDrone", spawnRate: 0.3 },
      ],
    },
    {
      opening: [
        { enemy: "orbDrone", spawnRate: 0.3 },
        { enemy: "hornetDrone", spawnRate: 0.2 },
        { enemy: "mineDrone", spawnRate: 0.1 },
      ],
      pressure: [
        { enemy: "orbDrone", spawnRate: 0.2 },
        { enemy: "hornetDrone", spawnRate: 0.2 },
        { enemy: "mineDrone", spawnRate: 0.3 },
      ],
    },
    {
      opening: [
        { enemy: "dwarf", spawnRate: 0.3 },
        { enemy: "hornetDrone", spawnRate: 0.2 },
        { enemy: "mineDrone", spawnRate: 0.1 },
      ],
      pressure: [
        { enemy: "dwarf", spawnRate: 0.2 },
        { enemy: "hornetDrone", spawnRate: 0.2 },
        { enemy: "mineDrone", spawnRate: 0.3 },
      ],
    },
    {
      opening: [
        { enemy: "orbDrone", spawnRate: 0.4 },
        { enemy: "dwarf", spawnRate: 0.2 },
      ],
      climax: [
        { enemy: "orbDrone", spawnRate: 0.2 },
        { enemy: "dwarf", spawnRate: 0.6 },
      ],
      boss: "humanoid",
    },
  ],
  boss: {
    type: "humanoid",
    name: "APEX COMMANDER",
    intro: "APEX COMMANDER ENGAGED.",
    hp: 2000,
    damageMultiplier: 0.8,
    pointsMultiplier: 4,
    introScale: 1.5,
  },
}

// ── SECTOR 2: LUNAR CORRIDOR (waves 5-8, boss: snake) ───────

const LUNAR_CORRIDOR: SectorDefinition = {
  id: "lunar-corridor",
  name: "Lunar Corridor",
  description: "Approach the lunar base.",
  waveStart: 5,
  waveEnd: 8,
  scrollMultiplier: 1.32,
  enemyDensityMultiplier: 1.25,
  treatMultiplier: 1.05,
  bossWave: 8,
  bumperDensityMultiplier: 1.05,
  waves: [
    {
      opening: [
        { enemy: "router", spawnRate: 0.6 },
        { enemy: "spikeMine", spawnRate: 0.4 },
      ],
      pressure: [
        { enemy: "router", spawnRate: 0.45 },
        { enemy: "spikeMine", spawnRate: 0.55 },
      ],
    },
    {
      opening: [
        { enemy: "router", spawnRate: 0.4 },
        { enemy: "spikeMine", spawnRate: 0.35 },
        { enemy: "chainDrifter", spawnRate: 0.25 },
      ],
      pressure: [
        { enemy: "router", spawnRate: 0.3 },
        { enemy: "spikeMine", spawnRate: 0.3 },
        { enemy: "chainDrifter", spawnRate: 0.4 },
      ],
    },
    {
      opening: [
        { enemy: "spikeMine", spawnRate: 0.55 },
        { enemy: "chainDrifter", spawnRate: 0.45 },
      ],
      climax: [
        { enemy: "spikeMine", spawnRate: 0.4 },
        { enemy: "chainDrifter", spawnRate: 0.6 },
      ],
    },
    {
      opening: [
        { enemy: "router", spawnRate: 0.55 },
        { enemy: "chainDrifter", spawnRate: 0.45 },
      ],
      climax: [
        { enemy: "router", spawnRate: 0.4 },
        { enemy: "chainDrifter", spawnRate: 0.6 },
      ],
      boss: "snake",
    },
  ],
  boss: {
    type: "snake",
    name: "LUNAR SERPENT",
    intro: "LUNAR SERPENT DETECTED.",
    hp: 4350,
    damageMultiplier: 2.0,
    pointsMultiplier: 4.5,
    introScale: 2,
  },
}

// ── SECTOR 3: CORE PERIMETER (waves 9-12, boss: brain) ───────

const CORE_PERIMETER: SectorDefinition = {
  id: "core-perimeter",
  name: "Core Perimeter",
  description: "The final defense wall.",
  waveStart: 9,
  waveEnd: 12,
  scrollMultiplier: 1.58,
  enemyDensityMultiplier: 1.42,
  treatMultiplier: 1.18,
  bossWave: 12,
  bumperDensityMultiplier: 1.2,
  waves: [
    {
      opening: [
        { enemy: "healer", spawnRate: 0.65 },
        { enemy: "kamikaze", spawnRate: 0.35 },
      ],
      pressure: [
        { enemy: "healer", spawnRate: 0.5 },
        { enemy: "kamikaze", spawnRate: 0.5 },
      ],
    },
    {
      opening: [
        { enemy: "healer", spawnRate: 0.4 },
        { enemy: "kamikaze", spawnRate: 0.35 },
        { enemy: "bomber", spawnRate: 0.25 },
      ],
      pressure: [
        { enemy: "healer", spawnRate: 0.3 },
        { enemy: "kamikaze", spawnRate: 0.4 },
        { enemy: "bomber", spawnRate: 0.3 },
      ],
    },
    {
      opening: [
        { enemy: "gunPod", spawnRate: 0.34 },
        { enemy: "bioPod", spawnRate: 0.33 },
        { enemy: "neonMoai", spawnRate: 0.33 },
      ],
      climax: [
        { enemy: "gunPod", spawnRate: 0.25 },
        { enemy: "bioPod", spawnRate: 0.3 },
        { enemy: "neonMoai", spawnRate: 0.45 },
      ],
    },
    {
      opening: [
        { enemy: "gunPod", spawnRate: 0.34 },
        { enemy: "bioPod", spawnRate: 0.33 },
        { enemy: "neonMoai", spawnRate: 0.33 },
      ],
      climax: [
        { enemy: "gunPod", spawnRate: 0.25 },
        { enemy: "bioPod", spawnRate: 0.3 },
        { enemy: "neonMoai", spawnRate: 0.45 },
      ],
      boss: "brain",
    },
  ],
  boss: {
    type: "brain",
    name: "NEURAL COMMANDER",
    intro: "NEURAL COMMANDER ONLINE.",
    hp: 5890,
    damageMultiplier: 2.2,
    pointsMultiplier: 5,
    introScale: 1.8,
  },
}

// ── Sector registry ────────────────────────────────────────────

export const SECTOR_WAVE_SPAN = 4

export const SECTORS: SectorDefinition[] = [
  OCCUPIED_SKYLINE,
  LUNAR_CORRIDOR,
  CORE_PERIMETER,
]

export const getSectorForWave = (wave: number): SectorDefinition => {
  const clampedWave = Math.max(1, Math.min(MAX_WAVES, wave))
  return (
    SECTORS.find(
      (sector) =>
        clampedWave >= sector.waveStart && clampedWave <= sector.waveEnd,
    ) ?? SECTORS[0]
  )
}

export const isBossWave = (wave: number): boolean => {
  return getSectorForWave(wave).bossWave === wave
}

export const getWaveConfig = (wave: number): WaveConfig => {
  const sector = getSectorForWave(wave)
  const waveInSector = wave - sector.waveStart
  return sector.waves[waveInSector] ?? { opening: [] }
}

export const getWaveEnemyPool = (
  wave: number,
  waveProgress = 0.5,
): WaveSpawnEntry[] => {
  const config = getWaveConfig(wave)
  const band = getWaveBandForProgress(wave, waveProgress)
  return resolvePool(config, band)
}

export const rollEnemyTypeForWave = (
  wave: number,
  waveProgress = 0.5,
): EnemyType => {
  return pickWeightedEnemy(getWaveEnemyPool(wave, waveProgress))
}

export const getWaveSpawnWeight = (
  wave: number,
  waveProgress = 0.5,
): number => {
  const pool = getWaveEnemyPool(wave, waveProgress)
  return pool.reduce((sum, entry) => sum + entry.spawnRate, 0)
}

export const getWaveBatchSize = (wave: number): number => {
  const config = getWaveConfig(wave)
  const sector = getSectorForWave(wave)
  return config.spawnBatchSize ?? sector.spawnBatchSize ?? (wave <= 2 ? 3 : 2)
}

export const getEnemyPopulationCap = (wave: number): number => {
  const config = getWaveConfig(wave)
  const sector = getSectorForWave(wave)
  return (
    config.populationCap ?? sector.populationCap ?? 8 + Math.floor(wave * 1.5)
  )
}

export const getEnemiesForWave = (wave: number): EnemyType[] => {
  return getWaveConfig(wave).opening.map((entry) => entry.enemy)
}

export const getBossForWave = (
  wave: number,
): { type: EnemyType; name: string; intro: string } | null => {
  const sector = getSectorForWave(wave)
  return {
    type: sector.boss.type,
    name: sector.boss.name,
    intro: sector.boss.intro,
  }
}
